/**
 * Top-level data-router (createBrowserRouter).
 *
 * Public auth routes (`auth/return`, `auth/error`, `auth/signed-out`,
 * `auth/session-expired`) render outside the AuthGate, as does the dev-only
 * `/theme-preview` gallery (DNKADM-0297 — DEV builds only, see below). Everything else sits under the AuthGate boot guard + the
 * AppLayout shell. The index route redirects home (`HOME_PATH`, DNKADM-0288).
 *
 * Route map (DNKADM-0006 U3) — the full design route list:
 *  - Operations (any authenticated operator): `members`, `members/:contactId`,
 *    `reservations` (DNKADM-0287 — tabbed Reservations destination: the calendar
 *    is the index route, the confirmation queue is the `pending` tab;
 *    `/confirmations` redirects to the tab), `activity`, `transaction-ledger`,
 *    `policies` (the read-only operator policy reference).
 *  - Configuration (Owner/Admin only, wrapped in `RequireAdmin`): `settings/payments`
 *    (DNKADM-0110 — tabbed Payments settings: sub-tabs `connection|payment-methods`,
 *    index renders connection for the OAuth return),
 *    `membership-credits` (sub-tabs `plans|packages|credit-buckets|daily-caps|settings`,
 *    DNKADM-0085 folded the former standalone Credit buckets + Daily caps screens in
 *    as tabs; `/credit-buckets` + `/daily-caps` redirect into them; DNKADM-0102 added
 *    the Packages tab), `cancellation-policy` (the cancellation-bands editor),
 *    `member-profile-fields` (DNKADM-0058 — the v2 registry that
 *    superseded the retired v1 `custom-fields` screen, DNKADM-0074), `platform-members`
 *    (DNKADM-0015 — the saas-auth login roster), `settings`, `config-audit`
 *    (DNKADM-0024 — the config-change audit log).
 * Not-yet-built screens render the themed `ComingSoon` placeholder; the shipped
 * Members/Reservations/MemberDetail screens are re-homed at their existing paths.
 *
 * Ask Dink (DNKADM-0434): every routable, non-detail screen carries
 * `handle: { askDink: { title, description } }` — this file is the one source of
 * truth for the screens the assistant may link to (`src/features/ask-dink/routeCatalogue.ts`
 * builds the caller's list from it; a guard test fails on a screen without it). A
 * route that only forwards somewhere opts out with `askDink: false`.
 */
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { HOME_PATH } from '@/routes/home';
import { RootLayout } from '@/routes/RootLayout';
import { RouteError } from '@/routes/RouteError';
import { AuthReturn } from '@/routes/AuthReturn';
import { AuthError } from '@/routes/AuthError';
import { SignedOut } from '@/routes/SignedOut';
import { SessionExpired } from '@/routes/SessionExpired';
import { NotFound } from '@/routes/NotFound';
import { AuthGate } from '@/auth/AuthGate';
import { AppLayout } from '@/components/AppLayout';
import { RequireAdmin } from '@/components/RequireAdmin';
import { MembersList } from '@/features/members/MembersList';
import { MemberDetail } from '@/features/members/MemberDetail';
import { MEMBER_DETAIL_ROUTE } from '@/utils/memberPath';
import { ReservationsScreen } from '@/features/reservations/ReservationsScreen';
import { ReservationsCalendar } from '@/features/reservations/calendar/ReservationsCalendar';
import { ConfirmationQueueScreen } from '@/features/confirmations/ConfirmationQueueScreen';
import { MyScheduleScreen } from '@/features/my-schedule/MyScheduleScreen';
import { CreditCardSettingsScreen } from '@/features/connections/CreditCardSettingsScreen';
import { PaymentMethodsScreen } from '@/features/connections/PaymentMethodsScreen';
import { PaymentsConnectionScreen } from '@/features/payments/PaymentsConnectionScreen';
import { PaymentsOverviewTab } from '@/features/payments/PaymentsOverviewTab';
import { PaymentsSettingsIndexRedirect } from '@/features/payments/PaymentsSettingsIndexRedirect';
import { MembershipCreditsScreen } from '@/features/membership-credits/MembershipCreditsScreen';
import { SettingsTab } from '@/features/membership-credits/SettingsTab';
import { PlansTab } from '@/features/membership-credits/PlansTab';
import { PlanDetail } from '@/features/membership-credits/PlanDetail';
import { PackagesTab } from '@/features/membership-credits/PackagesTab';
import { PoliciesScreen } from '@/features/policies/PoliciesScreen';
import { PolicyReferenceScreen } from '@/features/policy-reference/PolicyReferenceScreen';
import { SettingsScreen } from '@/features/settings/SettingsScreen';
import { TenantSettingsScreen } from '@/features/tenant-settings/TenantSettingsScreen';
import { ActivityLog } from '@/features/activity/ActivityLog';
import { PlatformMembersScreen } from '@/features/platform-members/PlatformMembersScreen';
import { DailyCapsScreen } from '@/features/daily-caps/DailyCapsScreen';
import { LocationsScreen } from '@/features/locations/LocationsScreen';
import { LocationDetail } from '@/features/locations/LocationDetail';
import { ServiceCategoriesScreen } from '@/features/service-categories/ServiceCategoriesScreen';
import { CreditBucketsScreen } from '@/features/credit-buckets/CreditBucketsScreen';
import { StaffScreen } from '@/features/staff/StaffScreen';
import { StaffDetail } from '@/features/staff/StaffDetail';
import { ResourcesScreen } from '@/features/resources/ResourcesScreen';
import { ResourceDetail } from '@/features/resources/ResourceDetail';
import { ServicesCatalogScreen } from '@/features/services/ServicesCatalogScreen';
import { ServicesScreen } from '@/features/services/ServicesScreen';
import { ServiceDetail } from '@/features/services/ServiceDetail';
import { MemberProfileFieldsScreen } from '@/features/member-profile-fields/MemberProfileFieldsScreen';
import { ConfigAuditLog } from '@/features/config-audit/ConfigAuditLog';
import { TransactionLedgerScreen } from '@/features/transaction-ledger/TransactionLedgerScreen';

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public auth routes (no AuthGate).
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/error', element: <AuthError /> },
      { path: 'auth/signed-out', element: <SignedOut /> },
      { path: 'auth/session-expired', element: <SessionExpired /> },

      // Dev-only theme preview gallery (DNKADM-0297 U1) — an `auth/*` sibling:
      // under RootLayout (theme provider) but OUTSIDE the AuthGate (no session,
      // no bootstrap, no API). `import.meta.env.DEV` is statically false in a
      // production build, so the whole branch — including the dynamic import,
      // hence the component module — is eliminated from the bundle; the
      // post-build assertion in `scripts/assert-preview-excluded.mjs` verifies
      // that instead of assuming it. Not a product surface: no nav entry links
      // here.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'theme-preview',
              lazy: async () => ({
                Component: (await import('@/routes/ThemePreview')).ThemePreview,
              }),
            },
            // Dev-only Now & Next board preview (DNKADM-0298) — same
            // mechanics: DEV-gated, lazy, bootstrap-bypassed, asserted absent
            // from production builds.
            {
              path: 'board-preview',
              lazy: async () => ({
                Component: (await import('@/routes/BoardPreview')).BoardPreview,
              }),
            },
          ]
        : []),

      // Everything else is gated by auth (boot guard) + the AppLayout shell.
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to={HOME_PATH} replace /> },

              // ---- Operations (available to any authenticated operator) ----
              // The Users roster, the same screen for every operator (DNKADM-0375: STAFF
              // is front of house, so the DNKADM-0361 read-only STAFF lookup is gone).
              {
                path: 'members',
                element: <MembersList />,
                handle: {
                  askDink: {
                    title: 'Members',
                    description:
                      'Find members and their details, invite a customer and sell to a walk-in; owners and admins also see prospects',
                  },
                },
              },
              { path: MEMBER_DETAIL_ROUTE, element: <MemberDetail /> },
              // Reservations + Pending (DNKADM-0287): the tabbed parent hosts the
              // calendar and the confirmation queue. The calendar is the INDEX
              // ROUTE — deliberately NOT an index redirect — so the
              // `/reservations?reservation=<uuid>` deep link (DNKADM-0147)
              // arrives with its query string untouched.
              {
                path: 'reservations',
                element: <ReservationsScreen />,
                children: [
                  {
                    index: true,
                    element: <ReservationsCalendar />,
                    handle: {
                      askDink: {
                        title: 'Reservations',
                        description:
                          'Booking calendar by day or week: book, move, check in or cancel a reservation',
                      },
                    },
                  },
                  {
                    path: 'pending',
                    element: <ConfirmationQueueScreen />,
                    handle: {
                      askDink: {
                        title: 'Confirmations',
                        description:
                          'Bookings waiting for confirmation: confirm, decline, edit or reassign them',
                      },
                    },
                  },
                ],
              },
              // Confirmations folded into Reservations as the Pending tab
              // (DNKADM-0287): keep old links/bookmarks working with a redirect.
              {
                path: 'confirmations',
                element: <Navigate to="/reservations/pending" replace />,
              },
              // Upcoming charges folded into the Transaction ledger Payments tab
              // (DNKADM-0154 / DNKADM-0377): the standalone screen/route is retired;
              // keep old links/bookmarks working with a redirect to Payments.
              {
                path: 'upcoming-charges',
                element: <Navigate to="/transaction-ledger?tab=payments" replace />,
              },
              // My schedule (DNKADM-0363) — a linked staff member's own hours,
              // locations, date exceptions and bookings, composed client-side from
              // STAFF-readable reads plus the `GET /v2/me/staff` resolver. UNGATED
              // like its Reservations neighbour: every read it makes is open to the
              // STAFF floor, and the screen renders the server's own answer for a
              // session with no coach record (`404`) or no roster row (`403`).
              {
                path: 'my-schedule',
                element: <MyScheduleScreen />,
                handle: {
                  askDink: {
                    title: 'My schedule',
                    description: 'Your own coaching hours, locations, time off and bookings',
                  },
                },
              },
              {
                path: 'activity',
                element: <ActivityLog />,
                handle: {
                  askDink: {
                    title: 'Activity log',
                    description: 'History of reservation and member actions, with filters',
                  },
                },
              },
              // Transaction ledger (DNKADM-0149; money-only DNKADM-0154; DNKADM-0377):
              // Payments + Upcoming (card-on-file) + Upcoming memberships — an
              // Operations item like Activity log, NOT behind RequireAdmin.
              {
                path: 'transaction-ledger',
                element: <TransactionLedgerScreen />,
                handle: {
                  askDink: {
                    title: 'Transaction ledger',
                    description:
                      'Payments received, upcoming card charges and upcoming membership renewals',
                  },
                },
              },
              // The operator Policies reference is an Operations read surface. It
              // intentionally owns `/policies`; the cancellation editor moved below.
              {
                path: 'policies',
                element: <PolicyReferenceScreen />,
                handle: {
                  askDink: {
                    title: 'Policies',
                    description:
                      'Read-only summary of the booking, cancellation and credit rules in effect',
                  },
                },
              },

              // ---- Configuration (Owner/Admin only) ----
              {
                element: <RequireAdmin />,
                children: [
                  // Payments (DNKADM-0110; Overview landing added DNKADM-0132): the
                  // settings/payments route is the fixed OAuth return. Its index gate
                  // forwards `?connect=...` to the Connection editor (query preserved
                  // so the toast + context restore still fire) and every other visit
                  // to the Overview status dashboard.
                  {
                    path: 'settings/payments',
                    element: <CreditCardSettingsScreen />,
                    children: [
                      // Forwards to Overview or Connection; not a screen of its own.
                      {
                        index: true,
                        element: <PaymentsSettingsIndexRedirect />,
                        handle: { askDink: false },
                      },
                      {
                        path: 'overview',
                        element: <PaymentsOverviewTab />,
                        handle: {
                          askDink: {
                            title: 'Payments overview',
                            description:
                              'Card payment provider connection for the business and each location',
                          },
                        },
                      },
                      // The fixed OAuth return; it forwards into Overview, so it is not offered.
                      {
                        path: 'connection',
                        element: <PaymentsConnectionScreen />,
                        handle: { askDink: false },
                      },
                      {
                        path: 'payment-methods',
                        element: <PaymentMethodsScreen />,
                        handle: {
                          askDink: {
                            title: 'Manual payment methods',
                            description: 'Add or edit manual payment methods such as cash or check',
                          },
                        },
                      },
                    ],
                  },
                  {
                    path: 'connections',
                    element: <Navigate to="/settings/payments" replace />,
                  },
                  {
                    path: 'connections/connection',
                    element: <Navigate to="/settings/payments/connection" replace />,
                  },
                  {
                    path: 'connections/payment-methods',
                    element: <Navigate to="/settings/payments/payment-methods" replace />,
                  },
                  {
                    path: 'membership-credits',
                    element: <MembershipCreditsScreen />,
                    children: [
                      { index: true, element: <Navigate to="plans" replace /> },
                      {
                        path: 'plans',
                        element: <PlansTab />,
                        handle: {
                          askDink: {
                            title: 'Plans',
                            description:
                              'Create, price and publish membership plans and the credits they deposit',
                          },
                        },
                      },
                      // The plan page (DNKADM-0496): read-only cards, one drawer each. Under
                      // `plans/` so the Plans tab stays selected.
                      { path: 'plans/:planId', element: <PlanDetail /> },
                      // Packages (DNKADM-0102): one-time offerings (kind=package) with
                      // by-bucket credit grants — the sibling of Plans (no redirect; new path).
                      {
                        path: 'packages',
                        element: <PackagesTab />,
                        handle: {
                          askDink: {
                            title: 'Packages',
                            description: 'One-time credit packages members can buy',
                          },
                        },
                      },
                      // Credit buckets + Daily caps folded in as tabs (DNKADM-0085):
                      // the former standalone screens render unchanged as tab bodies.
                      {
                        path: 'credit-buckets',
                        element: <CreditBucketsScreen />,
                        handle: {
                          askDink: {
                            title: 'Credit buckets',
                            description:
                              'Credit types, their price per credit and what they can book',
                          },
                        },
                      },
                      {
                        path: 'daily-caps',
                        element: <DailyCapsScreen />,
                        handle: {
                          askDink: {
                            title: 'Daily caps',
                            description: 'Limit how many hours can be booked per day',
                          },
                        },
                      },
                      {
                        path: 'settings',
                        element: <SettingsTab />,
                        handle: {
                          askDink: {
                            title: 'Plan settings',
                            description: 'Membership grace days and refund rounding',
                          },
                        },
                      },
                    ],
                  },
                  {
                    path: 'cancellation-policy',
                    element: <PoliciesScreen />,
                    handle: {
                      askDink: {
                        title: 'Cancellation refund bands',
                        description:
                          'Cancellation rules: how much is refunded depending on how far ahead a booking is cancelled',
                      },
                    },
                  },
                  // Daily caps folded into Membership & credits (DNKADM-0085): keep old
                  // links/bookmarks working with a redirect to the new tab.
                  {
                    path: 'daily-caps',
                    element: <Navigate to="/membership-credits/daily-caps" replace />,
                  },
                  {
                    path: 'locations',
                    element: <LocationsScreen />,
                    handle: {
                      askDink: {
                        title: 'Locations',
                        description:
                          'Add or edit locations; open one to set its hours of operation and tax',
                      },
                    },
                  },
                  // The Location detail is one page of cards (DNKADM-0495; it was two tabs,
                  // DNKADM-0080). The old `/hours` and `/tax` tab links still open it.
                  {
                    path: 'locations/:locationId',
                    element: <LocationDetail />,
                    children: [
                      { path: 'hours', element: null },
                      { path: 'tax', element: null },
                    ],
                  },
                  // The former standalone `/service-categories` screen now lives as a
                  // tab of the consolidated Services destination (DNKADM-0079); keep
                  // old links/bookmarks working with a redirect to that tab.
                  {
                    path: 'service-categories',
                    element: <Navigate to="/services/categories" replace />,
                  },
                  // Credit buckets folded into Membership & credits (DNKADM-0085): keep
                  // old links/bookmarks working with a redirect to the new tab.
                  {
                    path: 'credit-buckets',
                    element: <Navigate to="/membership-credits/credit-buckets" replace />,
                  },
                  // Hours of operation folded into the Location detail (DNKADM-0080):
                  // the standalone screen is retired; keep old links/bookmarks working
                  // by redirecting to the Locations list (open a location → Hours tab).
                  {
                    path: 'operating-hours',
                    element: <Navigate to="/locations" replace />,
                  },
                  {
                    path: 'staff',
                    element: <StaffScreen />,
                    handle: {
                      askDink: {
                        title: 'Staff',
                        description:
                          'Add or edit coaches and staff; open one to set schedules, time off and caps',
                      },
                    },
                  },
                  { path: 'staff/:staffId', element: <StaffDetail /> },
                  {
                    path: 'resources',
                    element: <ResourcesScreen />,
                    handle: {
                      askDink: {
                        title: 'Resources',
                        description:
                          'Bays and courts: add or edit them; open one to set its schedule',
                      },
                    },
                  },
                  { path: 'resources/:resourceId', element: <ResourceDetail /> },
                  // Consolidated Services destination (DNKADM-0079): a tabbed
                  // container with the grouped Services catalog (index/default) and
                  // the Service categories CRUD as sibling tabs. The fixed
                  // `categories` child out-ranks the `:serviceId` param, so
                  // `/services/categories` is the tab and `/services/<id>` is the
                  // (untouched) detail route.
                  {
                    path: 'services',
                    element: <ServicesCatalogScreen />,
                    children: [
                      {
                        index: true,
                        element: <ServicesScreen />,
                        handle: {
                          askDink: {
                            title: 'Services',
                            description:
                              'Service catalog: create, price, publish and copy services',
                          },
                        },
                      },
                      {
                        path: 'categories',
                        element: <ServiceCategoriesScreen />,
                        handle: {
                          askDink: {
                            title: 'Service categories',
                            description:
                              'Add, rename or reorder the categories services are grouped in',
                          },
                        },
                      },
                    ],
                  },
                  { path: 'services/:serviceId', element: <ServiceDetail /> },
                  {
                    path: 'member-profile-fields',
                    element: <MemberProfileFieldsScreen />,
                    handle: {
                      askDink: {
                        title: 'Member profile fields',
                        description: 'Custom fields shown on member profiles',
                      },
                    },
                  },
                  {
                    path: 'platform-members',
                    element: <PlatformMembersScreen />,
                    handle: {
                      askDink: {
                        title: 'Platform users',
                        description:
                          'Staff logins: invite people, set their role and location access',
                      },
                    },
                  },
                  {
                    path: 'settings',
                    element: <SettingsScreen />,
                    handle: {
                      askDink: {
                        title: 'Settings',
                        description:
                          'Your account, role and access summary, appearance and cache refresh',
                      },
                    },
                  },
                  {
                    path: 'tenant-settings',
                    element: <TenantSettingsScreen />,
                    handle: {
                      askDink: {
                        title: 'Tenant settings',
                        description: 'Business-wide booking settings and the currency',
                      },
                    },
                  },
                  {
                    path: 'config-audit',
                    element: <ConfigAuditLog />,
                    handle: {
                      askDink: {
                        title: 'Config changes',
                        description: 'History of configuration changes and who made them',
                      },
                    },
                  },
                ],
              },

              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(appRoutes);
